import {
  includes,
  get,
  map,
  flow,
  kebabCase,
  toLower,
  replace,
  trim,
  some,
  isFinite,
} from 'lodash';

import { SUPPORTED_LOCALES } from 'constants/locales';
import { GENDERS, FORMATTED_GENDERS, MASTER_PRODUCTS, EDITIONS } from 'constants/merchandising';
import { MSRP_PRICE_TIER } from 'constants/pdp';
import Model from 'models';
import I18n from 'utils/I18n';
import Money from 'utils/Money';
import Settings from 'utils/Settings';
import { generateProductUrl } from 'utils/Url';

import { UNISIZE_PRODS, GIFT_MESSAGE_DENYLIST } from './config';

const Product = class extends Model {
  get id() {
    return get(this._data, 'id');
  }

  get handle() {
    return get(this._data, 'handle');
  }

  get masterId() {
    return get(this._data, 'masterId');
  }

  get isGiftCard() {
    return includes(
      [
        MASTER_PRODUCTS.DIGITAL_GIFT_CARD,
        MASTER_PRODUCTS.PHYSICAL_GIFT_CARD,
        MASTER_PRODUCTS.PHYSICAL_GIFT_CARD_BOOKLET,
      ],
      this.masterId
    );
  }

  get isDigitalGiftCard() {
    return includes([MASTER_PRODUCTS.DIGITAL_GIFT_CARD], this.masterId);
  }

  get url() {
    return generateProductUrl(this.handle);
  }

  get description() {
    return this._localizedDescription || this._deprecatedDescription;
  }

  get _deprecatedDescription() {
    return get(this._data, 'description');
  }

  get _localizedDescription() {
    return get(this._data, ['localizedDescription', Settings.currentLocale]);
  }

  get sizeRecommendation() {
    return get(this._data, ['localizedSizeRecommendation', Settings.currentLocale]);
  }

  /**
   * "price" and "compareAtPrice" are only available for
   * ajax API. These may need to be updated for storefront.
   */

  get price() {
    return get(this._data, 'price');
  }

  get priceTier() {
    return get(this._data, 'priceTier');
  }

  get isSaleProduct() {
    if (!this.priceTier) return null;
    return !(this.priceTier === MSRP_PRICE_TIER);
  }

  get formattedPrice() {
    return new Money(this.price).format();
  }

  get compareAtPrice() {
    return get(this._data, 'compareAtPrice');
  }

  get formattedCompareAtPrice() {
    if (!this.compareAtPrice || !isFinite(this.compareAtPrice)) {
      return null;
    }
    return new Money(this.compareAtPrice).format();
  }

  get pageTitle() {
    return this._seoTitle || this.fullName;
  }

  get fullName() {
    return get(this._data, 'fullName');
  }

  get _seoTitle() {
    return get(this._data, ['localizedSeoTitle', Settings.currentLocale]);
  }

  get masterName() {
    return get(this._data, 'masterName');
  }

  get genericProductName() {
    let genericProductTitle = replace(this.masterName, FORMATTED_GENDERS.MENS, '');

    genericProductTitle = replace(genericProductTitle, FORMATTED_GENDERS.WOMENS, '');

    return trim(genericProductTitle);
  }

  get colorName() {
    return get(this._data, 'colorName');
  }

  get featuredImage() {
    return get(this._data, ['featuredImage']);
  }

  get laydownShotImage() {
    return get(this._data, ['laydownShotImage']);
  }

  get type() {
    return get(this._data, 'type');
  }

  get silhouette() {
    return get(this._data, 'silhouette');
  }

  get master() {
    return get(this._data, 'master');
  }

  get material() {
    return get(this._data, 'material');
  }

  get gender() {
    return get(this._data, 'gender');
  }

  get canonicalProduct() {
    return get(this._data, 'canonicalProduct');
  }

  get formattedGender() {
    switch (this.gender) {
      case GENDERS.MENS:
        return "Men's";
      case GENDERS.WOMENS:
        return "Women's";
      default:
        return 'Unisex';
    }
  }

  get isUnisize() {
    return !!get(UNISIZE_PRODS, this.masterId);
  }

  get edition() {
    const edition = get(this._data, 'edition', 'limited');

    if (this.priceTier !== 'msrp') {
      return EDITIONS.DEALS;
    }

    if (edition === EDITIONS.DEALS) {
      return EDITIONS.LIMITED;
    }

    return edition;
  }

  get colors() {
    return get(this._data, 'colors');
  }

  get swatchImageUrl() {
    return get(this._data, 'swatchImageUrl');
  }

  get hues() {
    const sanitize = flow([kebabCase, toLower]);

    return map(get(this._data, 'hues'), sanitize);
  }

  get defaultImage() {
    return get(this.images, 0);
  }

  get collectionProductCardImage() {
    const image = get(this._data, 'featuredImage') || this.defaultImage;
    return { ...image, alt: this.fullName };
  }

  get images() {
    return get(this._data, 'images');
  }

  set images(images) {
    this._data.images = images;
  }

  get contentfulImages() {
    return get(this._data, 'contentfulImages');
  }

  get modelDescription() {
    return (
      get(this._data, ['localizedModelDescription', Settings.currentLocale]) ||
      get(this._data, ['localizedModelDescription', SUPPORTED_LOCALES.EN_US])
    );
  }

  get sizes() {
    return get(this._data, 'sizes');
  }

  get sizesSortOrder() {
    return get(this._data, 'sizesSortOrder');
  }

  get sortedSizes() {
    return map(this.sizesSortOrder, sizeKey => get(this.sizes, sizeKey));
  }

  get isAvailable() {
    return some(this.sizes, ['available', true]);
  }

  get hasUnavailableSizes() {
    return some(this.sizes, ['available', false]);
  }

  get videoUrl() {
    return get(this._data, ['video', 'fields', 'file', 'url']);
  }

  get backorderMessage() {
    if (this._localizedBackorderMessage) {
      return this._localizedBackorderMessage;
    }

    if (this._deprecatedBackorderMessage) {
      return this._deprecatedBackorderMessage;
    }

    return I18n.localize('product.defaultBackorderMessage');
  }

  get _deprecatedBackorderMessage() {
    return get(this._data, 'backorderMessage');
  }

  get _localizedBackorderMessage() {
    return get(this._data, ['localizedBackorderMessage', Settings.currentLocale]);
  }

  get soldOutMessage() {
    if (this._localizedSoldOutMessage) {
      return this._localizedSoldOutMessage;
    }

    if (this._deprecatedSoldOutMessage) {
      return this._deprecatedSoldOutMessage;
    }

    return I18n.localize('product.defaultSoldOutMessage');
  }

  get _deprecatedSoldOutMessage() {
    return get(this._data, 'soldOutMessage');
  }

  get _localizedSoldOutMessage() {
    return get(this._data, ['localizedSoldOutMessage', Settings.currentLocale]);
  }

  get isElligibleForGiftMessage() {
    return !get(GIFT_MESSAGE_DENYLIST, this.masterId);
  }

  get enableBackInStockEmail() {
    return get(this._data, 'enableBackInStockEmail');
  }

  get bestFor() {
    return get(this._data, 'bestFor') || [];
  }

  get contentfulID() {
    return get(this._data, ['cfMetadata', 'colorwayEntry', 'id']);
  }

  get isShoprunnerEnabled() {
    return get(this._data, 'isShoprunnerEnabled');
  }

  get sizeHandles() {
    return map(this.sizes, size => size.handle);
  }

  get tags() {
    return get(this._data, 'tags');
  }

  get isFinalSale() {
    return includes(this.tags, 'FINAL SALE');
  }

  get _badgeVisibilityString() {
    return get(this._data, ['localizedBadgeVisibility', Settings.currentLocale], '').toLowerCase();
  }

  get _isBadgeVisible() {
    return this._badgeVisibilityString.includes('web');
  }

  get badge() {
    return this._isBadgeVisible
      ? get(this._data, ['localizedBadge', Settings.currentLocale], '')
      : null;
  }

  get showBadgeOnMobile() {
    return get(this._data, ['localizedBadgeShowOnMobile', Settings.currentLocale], false);
  }

  get dyExperiments() {
    return get(this._data, 'dy-experiments');
  }

  get galleryLifestyleImage() {
    return get(this._data, ['galleryLifestyleImage']);
  }
};

export default Product;
